import { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../utils/apiConfig';

/**
 * Custom hook to fetch ETF flow data (BTC, ETH, SOL)
 * Data is cached on backend and refreshed every 4 hours
 * @param {number} refreshInterval - Refresh interval in ms (default 5 minutes)
 */
export function useEtfFlows(refreshInterval = 5 * 60 * 1000) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/etf-flows`);

      if (!response.ok) {
        if (response.status === 503) {
          // Service initializing, not an error
          console.log('[ETF] Service initializing...');
          return;
        }
        throw new Error(`Failed to fetch ETF data: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch ETF data');
      }

      setData(result.data);
      setError(null);
    } catch (err) {
      console.error('[ETF] Fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Fetch immediately
    fetchData();

    // Set up refresh interval
    const interval = setInterval(fetchData, refreshInterval);

    return () => clearInterval(interval);
  }, [fetchData, refreshInterval]);

  // Helper to get flow for a specific date
  const getFlowByDate = useCallback((dateStr) => {
    if (!data?.history) return null;
    return data.history.find(h => h.date === dateStr);
  }, [data]);

  // Helper to format flow value
  const formatFlow = useCallback((value) => {
    if (value === null || value === undefined) return 'N/A';
    const absValue = Math.abs(value);
    const sign = value >= 0 ? '+' : '-';

    if (absValue >= 1e9) {
      return `${sign}$${(absValue / 1e9).toFixed(2)}B`;
    } else if (absValue >= 1e6) {
      return `${sign}$${(absValue / 1e6).toFixed(0)}M`;
    } else if (absValue >= 1e3) {
      return `${sign}$${(absValue / 1e3).toFixed(0)}K`;
    }
    return `${sign}$${absValue.toFixed(0)}`;
  }, []);

  return {
    data,
    loading,
    error,
    today: data?.today || {},
    history: data?.history || [],
    lastUpdate: data?.lastUpdate,
    refresh: fetchData,
    getFlowByDate,
    formatFlow,
  };
}

export default useEtfFlows;
